import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Button, Card, Text, Spinner, Input, Field, Textarea,
  Dialog, DialogSurface, DialogTitle, DialogBody, DialogActions, DialogContent,
  Dropdown, Option, makeStyles, tokens,
} from '@fluentui/react-components';
import {
  fetchIncompleteDrafts, completeIncompleteDraft, cancelIncompleteDraft, fetchTeamIncompleteDrafts,
  type CompleteIncompleteDraftInput, type SupervisedDraftEntry,
} from '../api/incomplete-drafts.api';
import type { OperationDraft, MissingFieldEntry } from '../types/api.types';
import { DateInput } from '../components/shared/DateInput';
import { NumberInput } from '../components/shared/NumberInput';
import { useT } from '../i18n';
import { usePermission } from '../hooks/usePermission';
import { PageTitle } from '../components/shared/PageTitle';
import { SubTabs } from '../components/shared/SubTabs';

const useStyles = makeStyles({
  page:  { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, padding: tokens.spacingVerticalL, maxWidth: '900px', margin: '0 auto' },
  card:  { padding: tokens.spacingVerticalM, marginBottom: tokens.spacingVerticalS, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: tokens.spacingHorizontalM, flexWrap: 'wrap' },
  form:  { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
});

function firstMissingField(draft: OperationDraft): MissingFieldEntry | undefined {
  return draft.proposed.flatMap(op => op.missingFields ?? [])[0];
}

function reasonText(t: ReturnType<typeof useT>, reason: MissingFieldEntry['reason']): string {
  switch (reason) {
    case 'NOT_SPECIFIED': return t.incompleteDrafts.reasonNotSpecified;
    case 'NOT_FOUND':     return t.incompleteDrafts.reasonNotFound;
    case 'RESTRICTED':    return t.incompleteDrafts.reasonRestricted;
    case 'AMBIGUOUS':     return t.incompleteDrafts.reasonAmbiguous;
    default:               return '';
  }
}

export function IncompleteDraftsPage() {
  const styles = useStyles();
  const t = useT();
  const queryClient = useQueryClient();
  const canSupervise = usePermission('operations:supervise');
  const [tab, setTab] = useState<'mine' | 'team'>('mine');
  const [activeDraft, setActiveDraft] = useState<OperationDraft | null>(null);
  const [activeOwnerName, setActiveOwnerName] = useState<string | undefined>();
  const [cancelTarget, setCancelTarget] = useState<OperationDraft | null>(null);
  const [form, setForm] = useState<CompleteIncompleteDraftInput>({});
  const [selectedCandidateId, setSelectedCandidateId] = useState<string | undefined>();

  const { data: drafts, isLoading } = useQuery({ queryKey: ['incomplete-drafts'], queryFn: fetchIncompleteDrafts });
  const { data: teamEntries, isLoading: isTeamLoading } = useQuery({
    queryKey: ['incomplete-drafts-team'],
    queryFn: fetchTeamIncompleteDrafts,
    enabled: canSupervise && tab === 'team',
  });

  const completeMutation = useMutation({
    mutationFn: (vars: { draftId: string; input: CompleteIncompleteDraftInput }) =>
      completeIncompleteDraft(vars.draftId, vars.input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['incomplete-drafts'] });
      queryClient.invalidateQueries({ queryKey: ['incomplete-drafts-team'] });
      setActiveDraft(null);
      setActiveOwnerName(undefined);
      setForm({});
      setSelectedCandidateId(undefined);
    },
  });

  const cancelMutation = useMutation({
    mutationFn: (draftId: string) => cancelIncompleteDraft(draftId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['incomplete-drafts'] });
      queryClient.invalidateQueries({ queryKey: ['incomplete-drafts-team'] });
      setCancelTarget(null);
    },
  });

  const openWizard = (draft: OperationDraft, ownerName?: string) => {
    setActiveDraft(draft);
    setActiveOwnerName(ownerName);
    setForm({});
    setSelectedCandidateId(undefined);
  };

  const submitComplete = () => {
    if (!activeDraft) return;
    completeMutation.mutate({
      draftId: activeDraft.draftId,
      input: selectedCandidateId ? { ...form, loteId: selectedCandidateId, loteName: undefined } : form,
    });
  };

  const renderMine = () => {
    if (isLoading) return <Spinner label={t.common.loading as string} />;
    return (
      <>
        {drafts?.length === 0 && <Text block>{t.incompleteDrafts.emptyState}</Text>}
        {drafts?.map(draft => {
          const missing = firstMissingField(draft);
          return (
            <Card key={draft.draftId} className={styles.card}>
              <div>
                <Text weight="semibold">{draft.proposed[0]?.type}</Text>
                <Text block>{missing && reasonText(t, missing.reason)}</Text>
              </div>
              <Button appearance="primary" onClick={() => openWizard(draft)}>
                {t.incompleteDrafts.completeButton}
              </Button>
            </Card>
          );
        })}
      </>
    );
  };

  const renderTeam = () => {
    if (isTeamLoading) return <Spinner label={t.common.loading as string} />;
    return (
      <>
        {teamEntries?.length === 0 && <Text block>{t.incompleteDrafts.teamEmptyState}</Text>}
        {teamEntries?.map((entry: SupervisedDraftEntry) => {
          const missing = firstMissingField(entry.draft);
          const ownerLabel = entry.ownerName ?? entry.ownerPhoneNumber;
          return (
            <Card key={entry.draft.draftId} className={styles.card}>
              <div>
                <Text weight="semibold">{t.incompleteDrafts.ownerLabel.replace('{name}', ownerLabel)}</Text>
                <Text block>{entry.draft.proposed[0]?.type}</Text>
                <Text block>{missing && reasonText(t, missing.reason)}</Text>
              </div>
              <Button appearance="primary" onClick={() => openWizard(entry.draft, ownerLabel)}>
                {t.incompleteDrafts.completeButton}
              </Button>
            </Card>
          );
        })}
      </>
    );
  };

  return (
    <div className={styles.page}>
      <PageTitle>{t.incompleteDrafts.pageTitle}</PageTitle>
      <Text block>{t.incompleteDrafts.pageDescription}</Text>

      {canSupervise && (
        <SubTabs selected={tab} onSelect={(v) => setTab(v as 'mine' | 'team')} tabs={[{ value: 'mine', label: t.incompleteDrafts.myTab }, { value: 'team', label: t.incompleteDrafts.teamTab }]} />
      )}

      {tab === 'mine' ? renderMine() : renderTeam()}

      <Dialog open={!!activeDraft} onOpenChange={(_, d) => !d.open && setActiveDraft(null)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>
              {activeOwnerName ? t.incompleteDrafts.ownerLabel.replace('{name}', activeOwnerName) : t.incompleteDrafts.completeButton}
            </DialogTitle>
            <DialogContent className={styles.form}>
              {activeDraft && (() => {
                const missing = firstMissingField(activeDraft);
                return (
                  <>
                    {missing?.reason === 'AMBIGUOUS' && missing.candidates ? (
                      <Field label={t.incompleteDrafts.fieldLote}>
                        <Dropdown
                          value={missing.candidates.find(c => c.loteId === selectedCandidateId)?.name ?? ''}
                          onOptionSelect={(_, opt) => setSelectedCandidateId(opt.optionValue)}
                        >
                          {missing.candidates.map(c => (
                            <Option key={c.loteId} value={c.loteId}>{`${c.name} (${c.ranchoName})`}</Option>
                          ))}
                        </Dropdown>
                      </Field>
                    ) : missing ? (
                      <Field label={t.incompleteDrafts.fieldLote}>
                        <Input value={form.loteName ?? ''} onChange={(_, d) => setForm(f => ({ ...f, loteName: d.value }))} />
                      </Field>
                    ) : null}

                    <Field label={t.incompleteDrafts.fieldQuantity}>
                      <NumberInput
                        value={form.totalCost?.toString() ?? ''}
                        onValueChange={(v) => setForm(f => ({ ...f, totalCost: v ? Number(v) : undefined }))}
                      />
                    </Field>
                    <Field label={t.incompleteDrafts.fieldDate}>
                      <DateInput
                        value={form.occurredAt?.slice(0, 10) ?? ''}
                        onValueChange={(v) => setForm(f => ({ ...f, occurredAt: v }))}
                      />
                    </Field>
                    <Field label={t.incompleteDrafts.fieldNotes}>
                      <Textarea value={form.notes ?? ''} onChange={(_, d) => setForm(f => ({ ...f, notes: d.value }))} />
                    </Field>
                  </>
                );
              })()}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => activeDraft && setCancelTarget(activeDraft)}>
                {t.incompleteDrafts.cancelButton}
              </Button>
              <Button appearance="primary" onClick={submitComplete}>
                {t.incompleteDrafts.saveButton}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <Dialog open={!!cancelTarget} onOpenChange={(_, d) => !d.open && setCancelTarget(null)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{t.incompleteDrafts.cancelConfirmTitle}</DialogTitle>
            <DialogContent>{t.incompleteDrafts.cancelConfirmBody}</DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setCancelTarget(null)}>
                {t.incompleteDrafts.cancelConfirmDismiss}
              </Button>
              <Button appearance="primary" onClick={() => cancelTarget && cancelMutation.mutate(cancelTarget.draftId)}>
                {t.incompleteDrafts.cancelConfirmConfirm}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
